import type { PersonalWalletSummary } from './types'
import { api, download, request } from './client'
import type {
  AdminTopUp,
  AdminTopUpPage,
  TopUpStatus,
  DailyReport,
  Expense,
  ExpenseCategory,
  ExpenseInput,
  ExpensePage,
  ExpenseStatus,
  ActiveSession,
  AuditLogPage,
  CarouselSlide,
  Charger,
  ChargerList,
  ChargerLog,
  ConnectorConsumption,
  Cpo,
  CpoAnalytics,
  CreditTransaction,
  Customer,
  LoginResult,
  NeaBill,
  NeaEstimate,
  NeaMeter,
  NeaPreviewResult,
  NeaRate,
  NeaReconciliationMonth,
  NeaTariff,
  NeaUploadResult,
  NeaUsage,
  NeaWindow,
  PlatformAnalytics,
  PlatformSettings,
  PricingPlan,
  RfidCard,
  SessionPage,
  Settlement,
  Station,
  User,
} from './types'

/** Endpoints that exist on the Saadhan Charge API today. */

type Q = Record<string, string | number | boolean | undefined | null>

// ── Auth ────────────────────────────────────────────────────────────────
export const auth = {
  login: (identifier: string, password: string) => api.post<LoginResult>('/auth/login', { identifier, password }),
  me: () => api.get<User>('/auth/me'),
  logout: () => api.post('/auth/logout'),
  uploadPicture: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ profile_picture: string; user: User }>('/auth/profile-picture', undefined, { form })
  },
  deletePicture: () => api.del<User>('/auth/profile-picture'),
}

// ── Chargers and connectors ─────────────────────────────────────────────
export interface ConnectorInput {
  connector_number: number
  connector_type: string
  max_power_kw?: number | null
  max_voltage_v?: number | null
  max_current_a?: number | null
  pricing_plan_id?: number | null
}
export interface ChargerInput {
  id?: string
  vendor?: string | null
  model?: string | null
  station_id?: number | null
  charger_type?: string | null
  max_power_kw?: number | null
  latitude?: number | null
  longitude?: number | null
  address?: string | null
  connectors?: ConnectorInput[]
}

export const chargers = {
  list: () => api.get<ChargerList>('/cpo/chargers', { page_size: 200 }),
  get: (id: string) => api.get<Charger>(`/cpo/chargers/${encodeURIComponent(id)}`),
  create: (body: ChargerInput) => api.post<Charger>('/cpo/chargers', body),
  update: (id: string, body: ChargerInput) => api.patch<Charger>(`/cpo/chargers/${encodeURIComponent(id)}`, body),
  remove: (id: string) => api.del(`/cpo/chargers/${encodeURIComponent(id)}`),
  online: () => api.get<{ chargers: Charger[]; total: number }>('/cpo/chargers/status/online'),
  activeSessions: (id: string) =>
    api.get<{ charger_id: string; active_sessions: ActiveSession[]; total_active: number }>(`/cpo/chargers/${encodeURIComponent(id)}/active-sessions`),
  // OCPP commands: arguments are query parameters; the server answers with a message.
  remoteStart: (id: string, connectorNumber: number, idTag: string) =>
    api.post<{ message?: string }>(`/cpo/chargers/${encodeURIComponent(id)}/remote-start`, undefined, { query: { connector_id: connectorNumber, id_tag: idTag } }),
  remoteStop: (id: string, transactionId: number) =>
    api.post<{ message?: string }>(`/cpo/chargers/${encodeURIComponent(id)}/remote-stop`, undefined, { query: { transaction_id: transactionId } }),
  reset: (id: string, type: 'Soft' | 'Hard') =>
    api.post<{ message?: string }>(`/cpo/chargers/${encodeURIComponent(id)}/reset`, undefined, { query: { reset_type: type } }),
  unlock: (id: string, connectorNumber: number) =>
    api.post<{ message?: string }>(`/cpo/chargers/${encodeURIComponent(id)}/connectors/${connectorNumber}/unlock`),
  clearCache: (id: string) => api.post<{ message?: string }>(`/cpo/chargers/${encodeURIComponent(id)}/clear-cache`),
  setConnectorStatus: (connectorDbId: number, status: string, reason?: string) =>
    api.put(`/cpo/connectors/${connectorDbId}/status`, undefined, { query: { status, reason } }),
  consumption: (q: { start_date?: string; end_date?: string }) => api.get<ConnectorConsumption>('/cpo/connectors/consumption', q),
}

// ── Sessions ────────────────────────────────────────────────────────────
export interface SessionFilters extends Q {
  charger_id?: string
  connector_number?: number
  station_id?: number
  user_id?: number
  id_tag?: string
  /** Exact transaction number. */
  transaction_id?: number
  status_filter?: string
  start_date?: string
  end_date?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  per_page?: number
}

export const sessions = {
  list: (q: SessionFilters) => api.get<SessionPage>('/cpo/sessions', q),
  anomalies: (q: SessionFilters) => api.get<SessionPage>('/cpo/anomalies', q),
  /** One session by transaction number, or null. Checks the number, so a backend that ignores the filter can't open the wrong session. */
  byTransaction: async (transactionId: number) =>
    (await api.get<SessionPage>('/cpo/sessions', { transaction_id: transactionId, per_page: 1 })).sessions.find((s) => s.transaction_id === transactionId) ?? null,
  export: (q: SessionFilters & { format: 'csv' | 'pdf' }) => download('/cpo/sessions/export', q, `sessions.${q.format}`),
  setStatus: (id: number, body: { status: string; reason?: string; stop_time_override?: boolean; force_update?: boolean }) =>
    api.patch(`/cpo/sessions/${id}/status`, body),
}

// ── Stations ────────────────────────────────────────────────────────────
export type StationInput = Partial<Omit<Station, 'id' | 'cpo_id' | 'photos' | 'created_at' | 'updated_at'>>

export const stations = {
  list: () => api.get<{ stations: Station[]; total: number }>('/cpo/stations', { page_size: 100 }),
  get: (id: number) => api.get<Station>(`/cpo/stations/${id}`),
  create: (body: StationInput) => api.post<Station>('/cpo/stations', body),
  update: (id: number, body: StationInput) => api.patch<Station>(`/cpo/stations/${id}`, body),
  remove: (id: number, force = false) => api.del(`/cpo/stations/${id}`, { query: { force } }),
  chargers: (id: number) => api.get<{ chargers: Charger[]; total: number }>(`/cpo/stations/${id}/chargers`, { page_size: 100 }),
  uploadPhotos: (id: number, files: File[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    return api.post(`/cpo/stations/${id}/photos`, undefined, { form })
  },
  deletePhoto: (id: number, photoId: number) => api.del(`/cpo/stations/${id}/photos/${photoId}`),
}

// ── Pricing plans ───────────────────────────────────────────────────────
export type PlanInput = Partial<Pick<PricingPlan, 'name' | 'plan_type' | 'description' | 'base_rate_per_kwh' | 'is_default' | 'is_active' | 'valid_from' | 'valid_until' | 'fallback_plan_id' | 'tod_intervals' | 'components'>>

export const pricing = {
  list: () => api.get<{ plans: PricingPlan[]; total: number }>('/cpo/pricing-plans', { page_size: 100, include_details: true }),
  get: (id: number) => api.get<PricingPlan>(`/cpo/pricing-plans/${id}`),
  create: (body: PlanInput) => api.post<PricingPlan>('/cpo/pricing-plans', body),
  update: (id: number, body: PlanInput) => api.patch<PricingPlan>(`/cpo/pricing-plans/${id}`, body),
  remove: (id: number) => api.del(`/cpo/pricing-plans/${id}`),
  defaultPlan: () => api.get<Pick<PricingPlan, 'id' | 'name' | 'plan_type' | 'base_rate_per_kwh'>>('/cpo/pricing-plans/default/current'),
  /** A bare array of connector database ids; null assigns every connector. */
  assign: (id: number, connectorIds: number[] | null) => api.put(`/cpo/pricing-plans/${id}/assign-to-connectors`, connectorIds),
}

// ── Customers, cards and credit ─────────────────────────────────────────
export const customers = {
  list: (q: { page?: number; per_page?: number; full_name_contains?: string; email_contains?: string; card_number_contains?: string; user_id?: number; is_active?: boolean }) =>
    api.get<{ users: Customer[]; total: number; page: number; total_pages: number }>('/cpo/users', q),
  /** One customer. With user_id the API answers with the customer itself (404 if
   *  they hold none of this operator's cards), not a page; a page is read too. */
  get: async (userId: number) => {
    const d = await api.get<Customer | { users?: Customer[] }>('/cpo/users', { user_id: userId })
    return ('users' in d ? d.users?.[0] : (d as Customer)) ?? null
  },
  all: () => api.get<{ users: User[]; total: number }>('/cpo/all-users'),
  assignStationRole: (userId: number, stationId: number, role: 'station_manager' | 'station_operator' | 'viewer') =>
    api.post(`/cpo/users/${userId}/assign-viewer`, undefined, { query: { station_id: stationId, role } }),
}

/** The signed-in user's own cards (any issuer, super cards included) — the
 *  driver endpoint, which every role may call, unlike /cpo/rfid-cards. */
export const myCards = {
  list: () => api.get<{ rfid_cards: Omit<RfidCard, 'user_id' | 'notes' | 'created_at'>[] }>('/user/rfid-balance').then((r) => r.rfid_cards),
}

export const cards = {
  list: (q: { user_id?: number; page?: number; page_size?: number }) =>
    api.get<{ cards: RfidCard[]; total: number; page: number; total_pages: number }>('/cpo/rfid-cards', q),
  create: (body: { card_number: string; user_id: number; expires_at?: string | null; notes?: string | null }) => api.post<RfidCard>('/cpo/rfid-cards', body),
  update: (id: number, body: Partial<Pick<RfidCard, 'is_active' | 'is_blocked' | 'expires_at' | 'notes'>>) => api.put<RfidCard>(`/cpo/rfid-cards/${id}`, body),
  remove: (id: number) => api.del(`/cpo/rfid-cards/${id}`),
}

export const credits = {
  add: (userId: number, amount: number, description?: string) => api.post('/cpo/credits/add', { user_id: userId, amount, description }),
  deduct: (userId: number, amount: number, description?: string) => api.post('/cpo/credits/deduct', { user_id: userId, amount, description }),
  transactions: (q: { user_id?: number; transaction_type?: string; created_after?: string; created_before?: string; page?: number; per_page?: number; sort_order?: string }) =>
    api.get<{ transactions: CreditTransaction[]; total: number; page: number; total_pages: number; current_balance?: number }>('/cpo/credits/transactions', q),
}

// ── Analytics, settlements, logs ────────────────────────────────────────
export const reports = {
  /** Date-only bounds are Nepal days: start inclusive, end exclusive. */
  analytics: (q: { start_date?: string; end_date?: string; station_id?: number; charger_id?: string }) => api.get<CpoAnalytics>('/cpo/analytics', q),
  settlements: () => api.get<Settlement>('/cpo/settlements'),
  logs: (q: { charger_id?: string; session_id?: number; event_type?: string; severity?: string; start_date?: string; end_date?: string; limit?: number; offset?: number }) =>
    api.get<{ logs: ChargerLog[]; total: number; limit: number; offset: number }>('/cpo/logs/', q),
}

// ── NEA electricity: bills (uploaded PDFs), meters, time-of-day analysis ──
const billFiles = (files: File[]) => {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))
  return form
}

export const nea = {
  meters: (q: { station_id?: number } = {}) => api.get<{ meters: NeaMeter[] }>('/cpo/nea/meters', q),
  updateMeter: (id: number, body: { station_id?: number | null; label?: string | null }) => api.patch<NeaMeter>(`/cpo/nea/meters/${id}`, body),
  deleteMeter: (id: number) => api.del(`/cpo/nea/meters/${id}`),
  /** Reads bill PDFs without saving them. */
  previewBills: (files: File[]) => api.post<{ results: NeaPreviewResult[] }>('/cpo/nea/bills/preview', undefined, { form: billFiles(files) }),
  /** Saves bill PDFs; each file is saved or refused on its own. */
  uploadBills: (b: { files: File[]; station_id?: number | null; replace?: boolean }) => {
    const form = billFiles(b.files)
    if (b.station_id) form.append('station_id', String(b.station_id))
    form.append('replace', String(!!b.replace))
    return api.post<{ results: NeaUploadResult[]; saved: number }>('/cpo/nea/bills', undefined, { form })
  },
  bills: (q: { station_id?: number; meter_id?: number; bill_year?: number } = {}) => api.get<{ bills: NeaBill[]; total: number }>('/cpo/nea/bills', q),
  /** Paid on a day, at one of NEA's steps (paid_percent) or a custom amount; paid_on null for unpaid. */
  setPaid: (id: number, body: { paid_on: string | null; paid_percent?: number | null; paid_amount?: string | null }) => api.patch<NeaBill>(`/cpo/nea/bills/${id}`, body),
  deleteBill: (id: number) => api.del(`/cpo/nea/bills/${id}`),
  tariff: () => api.get<NeaTariff>('/cpo/nea/tariff'),
  /** Whole Nepal days, or exact Nepal times (a billing cycle; no to_time runs to now). */
  usage: (q: { station_id?: number; from_date?: string; to_date?: string; from_time?: string; to_time?: string }) => api.get<NeaUsage>('/cpo/nea/usage', q),
  reconciliation: (station_id?: number) => api.get<{ station_id: number; months: NeaReconciliationMonth[] }>('/cpo/nea/reconciliation', { station_id }),
  estimate: (station_id?: number) => api.get<{ station_id: number; estimate: NeaEstimate | null }>('/cpo/nea/estimate', { station_id }),
}

/** Superadmin: NEA slot times, seasons and hand-set rates. */
export const neaAdmin = {
  tariff: () => api.get<NeaTariff>('/superadmin/nea/tariff'),
  saveSettings: (body: { windows: NeaWindow[]; seasons: Record<string, number[]> }) => api.put<NeaTariff>('/superadmin/nea/tariff/settings', body),
  setRate: (body: { category: string; season: string; slot: string; rate: string }) => api.put<NeaRate>('/superadmin/nea/tariff/rates', body),
  deleteRate: (id: number) => api.del(`/superadmin/nea/tariff/rates/${id}`),
}

// ── Station expenses ────────────────────────────────────────────────────
export interface ExpenseFilters extends Q {
  station_id?: number
  category_id?: number
  status?: ExpenseStatus
  /** Nepal calendar days, both inclusive. */
  from_date?: string
  to_date?: string
  page?: number
  per_page?: number
}
export const expenses = {
  categories: (includeHidden = false) => api.get<{ categories: ExpenseCategory[] }>('/cpo/expense-categories', { include_hidden: includeHidden || undefined }),
  addCategory: (name: string) => api.post<ExpenseCategory>('/cpo/expense-categories', { name }),
  updateCategory: (id: number, body: Partial<Pick<ExpenseCategory, 'name' | 'is_active' | 'sort_order'>>) => api.patch<ExpenseCategory>(`/cpo/expense-categories/${id}`, body),
  list: (q: ExpenseFilters) => api.get<ExpensePage>('/cpo/expenses', q),
  create: (body: ExpenseInput) => api.post<Expense>('/cpo/expenses', body),
  update: (id: number, body: Partial<Omit<ExpenseInput, 'station_id'>>) => api.patch<Expense>(`/cpo/expenses/${id}`, body),
  remove: (id: number) => api.del(`/cpo/expenses/${id}`),
  approve: (id: number) => api.post<Expense>(`/cpo/expenses/${id}/approve`),
  reject: (id: number, note: string) => api.post<Expense>(`/cpo/expenses/${id}/reject`, { note }),
  attachReceipt: (id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.put<Expense>(`/cpo/expenses/${id}/receipt`, undefined, { form })
  },
  removeReceipt: (id: number) => api.del<Expense>(`/cpo/expenses/${id}/receipt`),
  /** Receipts are private: fetched with the user's token, shown from a blob URL. */
  receiptUrl: async (id: number) => URL.createObjectURL(await (await request<Response>('GET', `/cpo/expenses/${id}/receipt`, { raw: true })).blob()),
  dailyReport: (q: { station_id?: number; from_date: string; to_date: string }) => api.get<DailyReport>('/cpo/expenses/daily-report', q),
  exportReport: (q: { station_id?: number; from_date: string; to_date: string; format: 'csv' | 'pdf' }) => download('/cpo/expenses/daily-report/export', q, `daily-report.${q.format}`),
}

// ── Superadmin ──────────────────────────────────────────────────────────
/**
 * GET /superadmin/cpos rows. The live API has sent a flat shape (cpo_id,
 * cpo_name, total_chargers…); newer versions also send id, name, user and
 * stats. Either way the screens get a Cpo.
 */
type RawCpo = Partial<Cpo> & {
  cpo_id?: number
  cpo_name?: string
  total_chargers?: number
  online_chargers?: number
  total_sessions?: number
  total_energy_kwh?: number
  total_revenue_usd?: number
}

export function normalizeCpo(r: RawCpo): Cpo {
  return {
    id: (r.id ?? r.cpo_id)!,
    user_id: r.user_id ?? 0,
    name: r.name ?? r.cpo_name ?? '',
    profile_picture: r.profile_picture ?? null,
    created_at: r.created_at ?? null,
    user: r.user,
    stats: r.stats ?? {
      total_chargers: r.total_chargers ?? 0,
      online_chargers: r.online_chargers ?? 0,
      total_sessions: r.total_sessions ?? 0,
      total_energy_kwh: r.total_energy_kwh ?? 0,
      total_revenue: r.total_revenue_usd ?? 0,
    },
  }
}

export const admin = {
  analytics: () => api.get<PlatformAnalytics>('/superadmin/analytics'),
  cpos: async (q: { page?: number; per_page?: number; name_contains?: string }) => {
    const r = await api.get<{ cpos: RawCpo[]; total: number; page: number; total_pages: number }>('/superadmin/cpos', q)
    return { ...r, cpos: r.cpos.map(normalizeCpo) }
  },
  createCpo: (body: { email: string; password: string; full_name: string; organization_name: string }) => api.post<Cpo>('/superadmin/cpos', body),
  deleteCpo: (id: number) => api.del(`/superadmin/cpos/${id}`),
  users: (q: { page?: number; per_page?: number; role?: string; is_active?: boolean; email_contains?: string; full_name_contains?: string }) =>
    api.get<{ users: User[]; total: number; page: number; total_pages: number }>('/superadmin/users', q),
  updateUser: (id: number, body: { email?: string | null; phone?: string | null; full_name?: string | null; is_active?: boolean; password?: string }) =>
    api.patch<User>(`/superadmin/users/${id}`, body),
  deleteUser: (id: number) => api.del(`/superadmin/users/${id}`),
  assignStationRole: (userId: number, stationId: number, role: string, cpoId?: number) =>
    api.post(`/superadmin/users/${userId}/assign-viewer`, undefined, { query: { station_id: stationId, role, cpo_id: cpoId } }),
  chargers: (q: { cpo_id?: number; status?: string; charger_id_contains?: string; page?: number; per_page?: number }) =>
    api.get<{ chargers: Charger[]; total: number; online: number; offline: number; page: number; total_pages: number }>('/superadmin/chargers', q),
  sessions: (q: Q) => api.get<SessionPage>('/superadmin/sessions', q),
  exportSessions: (q: Q & { format: 'csv' | 'pdf' }) => download('/superadmin/sessions/export', q, `sessions.${q.format}`),
  anomalies: (q: Q) => api.get<SessionPage>('/superadmin/anomalies', q),
  transactions: (q: Q) => api.get<{ transactions: CreditTransaction[]; total: number; page: number; total_pages: number }>('/superadmin/transactions', q),
  topUps: (q: { status?: TopUpStatus; user_id?: number; created_after?: string; created_before?: string; page?: number; per_page?: number }) =>
    api.get<AdminTopUpPage>('/superadmin/topups', q),
  /** Ask the gateway again now; credits the wallet if it now confirms the same amount. */
  recheckTopUp: (reference: string) => api.post<AdminTopUp>(`/superadmin/topups/${encodeURIComponent(reference)}/recheck`),
  superCards: (q: Q) => api.get<{ cards: RfidCard[]; total: number; page: number; total_pages: number }>('/superadmin/rfid-cards', q),
  issueSuperCard: (body: { card_number: string; user_id: number; expires_at?: string | null; notes?: string | null }) => api.post<RfidCard>('/superadmin/rfid-cards', body),
  updateSuperCard: (id: number, body: Partial<Pick<RfidCard, 'is_active' | 'is_blocked' | 'expires_at' | 'notes'>>) => api.put<RfidCard>(`/superadmin/rfid-cards/${id}`, body),
  settlements: () => api.get<Settlement>('/superadmin/settlements'),
  superCardActivity: (q: Q) => api.get<{ activity: CreditTransaction[]; total: number; page: number; total_pages: number }>('/superadmin/super-card-activity', q),
  settings: () => api.get<PlatformSettings>('/superadmin/settings'),
  updateSettings: (body: Partial<PlatformSettings>) => api.patch<PlatformSettings>('/superadmin/settings', body),
  addCredit: (userId: number, amount: number, description?: string) => api.post('/superadmin/credits/add', { user_id: userId, amount, description }),
  deductCredit: (userId: number, amount: number, description?: string) => api.post('/superadmin/credits/deduct', { user_id: userId, amount, description }),
  carousel: () => api.get<{ carousels: CarouselSlide[]; total: number }>('/superadmin/carousel'),
  createSlide: (form: FormData) => api.post<CarouselSlide>('/superadmin/carousel', undefined, { form }),
  updateSlide: (id: number, form: FormData) => api.put<CarouselSlide>(`/superadmin/carousel/${id}`, undefined, { form }),
  deleteSlide: (id: number) => api.del(`/superadmin/carousel/${id}`),
  smsCredit: () => api.get<Record<string, unknown>>('/superadmin/sms-credit'),
  push: (body: { title: string; body: string; data?: Record<string, string>; user_id?: number; user_ids?: number[]; role?: string; all_users?: boolean }) =>
    api.post<Record<string, unknown>>('/superadmin/push/custom', body),
}

// ── Audit log ───────────────────────────────────────────────────────────
export interface AuditFilters extends Q {
  /** Exact (`pricing_plan.updated`) or a group (`pricing_plan`). */
  action?: string
  entity_type?: string
  entity_id?: string
  actor_user_id?: number
  /** Dates are Nepal days, and end_date is inclusive. */
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
  /** Platform log only. */
  cpo_id?: number
  system_only?: boolean
}

export const audit = {
  list: (q: AuditFilters) => api.get<AuditLogPage>('/cpo/audit-logs/', q),
  platform: (q: AuditFilters) => api.get<AuditLogPage>('/superadmin/audit-logs/', q),
}

// Personal account endpoint: no CPO selection or customer ID is sent.
export const wallet = {
  summary: () => api.get<PersonalWalletSummary>('/user/rfid-balance').then((data) => {
    if (!Array.isArray(data.wallets) || data.wallets.some((item) => typeof item.balance !== 'number' || !Number.isFinite(item.balance))) throw new Error('Wallet balance could not be read')
    return data
  }),
}
